import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, AlertCircle, Wrench } from "lucide-react";

/* ───────── Push-to-Myntra button ─────────
 * Renders next to the Excel download CTA. On click, POSTs the
 * generated `job_id` to /seller/myntra/push-excel which reads the
 * Excel from disk and uploads it to Myntra's Partner Catalog API.
 *
 * Three states:
 *   1. Myntra not connected → button is disabled with a tooltip
 *      directing the seller to Profile → Myntra Partner API.
 *   2. Connected, idle → "Push to Myntra" button.
 *   3. After push → shows ✓ Pushed (taskId: ABC123) or ✗ Errors (N).
 *      If errors exist, an "Auto-fix tips" expand panel calls
 *      /seller/myntra/parse-errors and shows per-error suggestions.
 */
export default function PushToMyntraButton({ jobId }) {
  const [connected, setConnected] = useState(null); // null = unknown, true/false = known
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { ok, task_id, error_count, errors }
  const [parsed, setParsed] = useState(null); // parse-errors response
  const [showFixes, setShowFixes] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/seller/myntra/status")
      .then(({ data }) => { if (!cancelled) setConnected(!!data.connected); })
      .catch(() => { if (!cancelled) setConnected(false); });
    return () => { cancelled = true; };
  }, []);

  async function push() {
    if (!jobId) {
      toast.error("Generate an Excel first");
      return;
    }
    setBusy(true);
    setResult(null);
    setParsed(null);
    setShowFixes(false);
    try {
      const { data } = await api.post(
        "/seller/myntra/push-excel",
        { job_id: jobId },
        { timeout: 120000 }
      );
      setResult(data);
      if (data.ok) {
        toast.success(
          data.task_id
            ? `Pushed to Myntra ✓ Task: ${data.task_id}`
            : "Pushed to Myntra ✓"
        );
      } else if (data.error_count > 0) {
        toast.error(`Myntra rejected ${data.error_count} row(s) — see fix tips below`);
        // Fire-and-forget: get auto-fix suggestions for the errors
        try {
          const { data: pd } = await api.post(
            "/seller/myntra/parse-errors",
            { errors: data.errors || [] }
          );
          setParsed(pd);
          setShowFixes(true);
        } catch {
          // Non-fatal — UI just won't show suggestions
        }
      } else {
        toast.error("Myntra push failed — please retry");
      }
    } catch (e) {
      toast.error(errMsg(e, "Push to Myntra failed"));
    } finally {
      setBusy(false);
    }
  }

  if (connected === false) {
    return (
      <a
        href="/seller/profile"
        title="Connect Myntra in Profile to enable one-click push"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/10 hover:bg-white/20 text-white/60 text-xs font-bold border border-white/20"
        data-testid="push-myntra-disabled"
      >
        <AlertCircle size={12} /> Connect Myntra
      </a>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <button
          onClick={push}
          disabled={busy || connected === null}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-xs font-bold disabled:opacity-50"
          style={{ background: "linear-gradient(135deg, #FF3F6C 0%, #BE185D 100%)" }}
          data-testid="push-myntra-btn"
        >
          {busy ? (
            <><Loader2 size={12} className="animate-spin" /> Pushing…</>
          ) : result?.ok ? (
            <><CheckCircle2 size={12} /> Pushed</>
          ) : (
            <><Send size={12} /> Push to Myntra</>
          )}
        </button>
        {result?.ok && result?.task_id && (
          <span className="text-[10px] text-white/70 font-mono" data-testid="push-myntra-taskid">
            {result.task_id}
          </span>
        )}
        {result && !result.ok && result.error_count > 0 && (
          <button
            type="button"
            onClick={() => setShowFixes((v) => !v)}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-rose-500/15 hover:bg-rose-500/25 text-rose-200 text-[10px] font-bold border border-rose-400/30"
            data-testid="push-myntra-fixes-toggle"
          >
            <Wrench size={11} />
            {result.error_count} error{result.error_count === 1 ? "" : "s"} — {showFixes ? "hide" : "show"} fix tips
          </button>
        )}
      </div>

      {showFixes && parsed && parsed.items?.length > 0 && (
        <div
          className="rounded-lg border border-rose-400/30 bg-rose-950/30 p-3 max-h-72 overflow-y-auto"
          data-testid="push-myntra-fix-panel"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-widest font-bold text-rose-200">
              Rejection Auto-Fix Tips
            </div>
            <div className="text-[10px] text-rose-200/70">
              {parsed.auto_fixable_count} of {parsed.total} auto-fixable
            </div>
          </div>
          <ul className="space-y-2">
            {parsed.items.map((item, idx) => (
              <li
                key={idx}
                className="rounded-md border border-rose-400/20 bg-black/30 p-2 text-[11px]"
                data-testid={`push-myntra-fix-item-${idx}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-rose-200 font-mono">Row {item.row}</span>
                  {item.field && (
                    <span className="px-1.5 py-0.5 rounded bg-white/10 text-white/80 font-mono text-[9px] uppercase">
                      {item.field}
                    </span>
                  )}
                  <span
                    className={
                      "text-[9px] uppercase font-bold " +
                      (item.severity === "high"
                        ? "text-rose-300"
                        : item.severity === "medium"
                        ? "text-amber-300"
                        : "text-white/50")
                    }
                  >
                    {item.severity}
                  </span>
                </div>
                <div className="text-white/60 mb-1 italic line-clamp-2">"{item.error}"</div>
                <div className="text-white/85">{item.suggestion}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
